import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useToast } from '../../context/ToastContext';
import {
  fetchEscalations,
  createEscalation,
  updateEscalationStatus,
  fetchSupportThreads,
} from '../../lib/adminApi';

const statusFilters = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const priorityLabels = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
const statusStyles = {
  open: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-muted text-muted-foreground',
};

export default function EscalationCentrePage() {
  const { profile } = useAdminAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderIdFromQuery = searchParams.get('orderId');

  const [statusFilter, setStatusFilter] = useState('all');
  const [escalations, setEscalations] = useState([]);
  const [supportThreads, setSupportThreads] = useState([]);
  const [supportLoading, setSupportLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionState, setActionState] = useState({});

  // Create escalation modal (from order or standalone)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createOrderId, setCreateOrderId] = useState(null);
  const [createSubject, setCreateSubject] = useState('');
  const [createNotes, setCreateNotes] = useState('');
  const [createPriority, setCreatePriority] = useState('medium');
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Resolve/update modal
  const [updateModal, setUpdateModal] = useState(null);
  const [updateResolution, setUpdateResolution] = useState('');
  const [updateSubmitting, setUpdateSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchEscalations({
          status: statusFilter === 'all' ? undefined : statusFilter,
        });
        if (isMounted) setEscalations(data);
      } catch (err) {
        console.error('Failed to load escalations', err);
        if (isMounted) setError(err?.message ?? 'Failed to load escalations');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => { isMounted = false; };
  }, [statusFilter]);

  // Load open support threads so they appear as items needing attention
  useEffect(() => {
    let isMounted = true;
    setSupportLoading(true);
    fetchSupportThreads({ resolved: false })
      .then((data) => { if (isMounted) setSupportThreads(data ?? []); })
      .catch(() => { if (isMounted) setSupportThreads([]); })
      .finally(() => { if (isMounted) setSupportLoading(false); });
    return () => { isMounted = false; };
  }, []);

  // If URL has ?orderId=... open create modal for that order
  useEffect(() => {
    if (orderIdFromQuery && !showCreateModal) {
      setCreateOrderId(orderIdFromQuery);
      setCreateSubject('');
      setCreateNotes('');
      setCreatePriority('medium');
      setShowCreateModal(true);
    }
  }, [orderIdFromQuery]);

  const openCreateForOrder = (orderId) => {
    setCreateOrderId(orderId);
    setCreateSubject('');
    setCreateNotes('');
    setCreatePriority('medium');
    setShowCreateModal(true);
  };

  const handleCreateEscalation = async () => {
    if (!createOrderId || !profile?.user_id) {
      toast.error('Order and admin session required');
      return;
    }
    setCreateSubmitting(true);
    try {
      await createEscalation(
        {
          orderId: createOrderId,
          subject: createSubject || null,
          notes: createNotes || null,
          priority: createPriority,
        },
        profile.user_id
      );
      toast.success('Escalation created');
      setShowCreateModal(false);
      setCreateOrderId(null);
      setEscalations((prev) => [...prev].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      const data = await fetchEscalations({ status: statusFilter === 'all' ? undefined : statusFilter });
      setEscalations(data);
    } catch (err) {
      toast.error(err?.message ?? 'Failed to create escalation');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleUpdateStatus = async (escalationId, newStatus) => {
    setActionState((prev) => ({ ...prev, [escalationId]: true }));
    try {
      await updateEscalationStatus(
        escalationId,
        newStatus,
        newStatus === 'resolved' || newStatus === 'closed' ? updateResolution : null,
        profile?.user_id
      );
      toast.success('Escalation updated');
      setUpdateModal(null);
      const data = await fetchEscalations({ status: statusFilter === 'all' ? undefined : statusFilter });
      setEscalations(data);
    } catch (err) {
      toast.error(err?.message ?? 'Failed to update escalation');
    } finally {
      setActionState((prev) => ({ ...prev, [escalationId]: false }));
    }
  };

  const openUpdateModal = (esc) => {
    setUpdateModal(esc);
    setUpdateResolution(esc.resolution || '');
    setUpdateSubmitting(false);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Escalation Centre</h2>
          <p className="text-sm text-muted-foreground">
            Track and resolve order-related escalations. Create from an order or from here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setCreateOrderId(null);
            setCreateSubject('');
            setCreateNotes('');
            setCreatePriority('medium');
            setShowCreateModal(true);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
        >
          <Icon name="Plus" size={16} /> New escalation
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Open support threads: customer messages from order page appear here */}
      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden mb-6">
          <div className="border-b border-border px-4 py-3 flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Open support threads</h3>
            <button
              type="button"
              onClick={() => navigate('/admin/support')}
              className="text-sm font-medium text-primary hover:underline"
            >
              Reply in Support →
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {supportLoading ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : supportThreads.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">No open threads</div>
            ) : (
              <ul className="divide-y divide-border">
                {supportThreads.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => navigate('/admin/support')}
                      className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/50"
                    >
                      <span className="text-sm font-medium text-foreground">
                        Order: {t.order_id ? String(t.order_id).slice(0, 8) + '…' : '—'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t.created_at ? format(new Date(t.created_at), 'dd MMM yyyy HH:mm') : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        {statusFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === f.value ? 'bg-primary text-primary-foreground shadow' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Order</th>
                <th className="px-4 py-3 text-left font-semibold">Subject</th>
                <th className="px-4 py-3 text-left font-semibold">Priority</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Created</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                    Loading escalations…
                  </td>
                </tr>
              )}
              {!loading && escalations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                    No escalations match the selected filter.
                  </td>
                </tr>
              )}
              {!loading &&
                escalations.map((esc) => (
                  <tr key={esc.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-mono text-xs text-foreground">{esc.order?.id?.slice(0, 8)}…</span>
                        <span className="text-xs text-muted-foreground">
                          {esc.order?.caterer?.business_name ?? '—'} · {esc.order?.customer?.email ?? '—'}
                        </span>
                        {esc.order?.total_amount != null && (
                          <span className="text-xs text-muted-foreground">
                            R{Number(esc.order.total_amount).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{esc.subject || '—'}</p>
                      {esc.notes && (
                        <p className="mt-1 max-w-xs truncate text-xs text-muted-foreground" title={esc.notes}>
                          {esc.notes}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                        {priorityLabels[esc.priority] ?? esc.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                          statusStyles[esc.status] ?? 'bg-muted text-foreground'
                        }`}
                      >
                        {esc.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {esc.created_at ? format(new Date(esc.created_at), 'dd MMM yyyy HH:mm') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/orders`)}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
                        >
                          Order
                        </button>
                        {(esc.status === 'open' || esc.status === 'in_progress') && (
                          <>
                            {esc.status === 'open' && (
                              <button
                                type="button"
                                disabled={actionState[esc.id]}
                                onClick={() => handleUpdateStatus(esc.id, 'in_progress')}
                                className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-60"
                              >
                                In progress
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => openUpdateModal(esc)}
                              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-500"
                            >
                              Resolve
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create escalation modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCreateModal(false)}>
          <div
            className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-foreground">Create escalation</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {createOrderId ? `Order: ${String(createOrderId).slice(0, 8)}…` : 'Enter an order ID or create from the Orders page.'}
            </p>
            {!createOrderId && (
              <input
                type="text"
                placeholder="Order ID (UUID)"
                value={createOrderId ?? ''}
                onChange={(e) => setCreateOrderId(e.target.value.trim() || null)}
                className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            )}
            <input
              type="text"
              placeholder="Subject"
              value={createSubject}
              onChange={(e) => setCreateSubject(e.target.value)}
              className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
            <textarea
              placeholder="Notes"
              value={createNotes}
              onChange={(e) => setCreateNotes(e.target.value)}
              rows={3}
              className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
            <select
              value={createPriority}
              onChange={(e) => setCreatePriority(e.target.value)}
              className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            >
              {['low', 'medium', 'high', 'urgent'].map((p) => (
                <option key={p} value={p}>
                  {priorityLabels[p]}
                </option>
              ))}
            </select>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!createOrderId || createSubmitting}
                onClick={handleCreateEscalation}
                className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {createSubmitting ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve modal */}
      {updateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setUpdateModal(null)}>
          <div
            className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-foreground">Resolve escalation</h3>
            <p className="mt-1 text-sm text-muted-foreground">{updateModal.subject || 'No subject'}</p>
            <textarea
              placeholder="Resolution notes"
              value={updateResolution}
              onChange={(e) => setUpdateResolution(e.target.value)}
              rows={4}
              className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setUpdateModal(null)}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleUpdateStatus(updateModal.id, 'resolved')}
                disabled={updateSubmitting || actionState[updateModal.id]}
                className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
              >
                Resolve
              </button>
              <button
                type="button"
                onClick={() => handleUpdateStatus(updateModal.id, 'closed')}
                disabled={updateSubmitting || actionState[updateModal.id]}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
              >
                Close without resolution
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
